<template lang="pug">
TheCommentaryToolbar
div
  VContainer.commentary.pb-12
    VRow
      VCol.commentary__chips.d-flex.flex-wrap.align-center
        CommentaryTypeChip(
          v-model:commentary-type-short-name="commentaryTypeShortName"
          :counts="commentaryTypeCounts"
        )
        CommentaryDetailChip(
          v-model:detail-level-weight="commentaryFiltersStore.detailLevelWeight"
          :counts="detailLevelCounts"
        )
        CommentaryLanguagesChip(
          v-model:languages="commentaryLanguages"
          :counts="languageCounts"
        )
        VSpacer
        div(
          v-if="!commentaryQuery.isPending.value && !!numResults.text"
          v-html="i18n.t('Commentary.result', { count: numResults.text }, numResults.totalCount)"
        )

    VRow(v-if="commentaryQueryError")
      VCol
        // TODO: Retry button.
        ErrorAlert(
          :reporting="{ message: commentaryQueryError.message, extra: commentaryQueryError }"
          :message="commentaryQueryError.message"
          :stack="commentaryQueryError.stack"
        )

    VRow(v-else-if="commentaryQuery.isPending.value")
      VCol
        VSkeletonLoader.mb-4(type="heading")
        VSheet.mb-4.pa-4(:rounded="true")
          VSkeletonLoader(type="text@2")
        VSheet.pa-4(:rounded="true")
          VSkeletonLoader(type="paragraph")

    VRow(v-else-if="!outerGroups.length")
      VCol
        VAlert(type="info")
          i18n-t(
            keypath="Commentary.EmptyResultAlert"
            tag="div"
            scope="global"
          )
            template(#name)
              strong {{ commentaryType.name[locale] }}
            template(#value)
              strong {{ humanReadableRange.value }}

    VRow(v-else)
      VCol
        CommentaryGroupOuter(
          v-for="(outerGroup, key) in outerGroups"
          :key="key"
          :range="outerGroup.range"
          :heading="outerGroup.heading"
          :doclist="outerGroup.doclist"
          :sub-groups="outerGroup.subGroups"
          :local-tid="localTid"
          :commentary-type-name="commentaryType.name[locale] ?? commentaryType.shortName"
          :commentary-type-short-name="commentaryType.shortName"
        )
  hr

  VContainer.commentary.pb-14(
    v-if="!commentaryQuery.isPending.value && otherCommentaryTypes.length"
  )
    VRow
      VCol
        h2.section-title-2 {{ i18n.t("Commentary.OtherComments") }}
    VRow
      VCol(
        v-for="t in otherCommentaryTypes"
        :key="t.uuid"
      )
        CommentaryTypeSwitcher(
          :commentary-type-short-name="t.shortName"
          :commentary-type-name="t.name[locale] ?? t.shortName"
          :count="countData[t.shortName]"
          style="min-width: max-content"
        )

  // TODO: Alert user when comments are available at a higher detail level?
  hr
  VContainer.commentary.pt-5.pb-5
    h2.section-title-2.mt-5.mb-0
      | {{ i18n.t("Commentary.OtherReferences") }}
  div.pb-10
    IndexListScroller(
      v-if="firstVerse.value"
      :local-tid="localTid"
    )
    // TODO: Implement retry on error?
    div(v-else-if="firstVerse.error") {{ firstVerse.error.message }}
    VSkeletonLoader(
      v-else-if="firstVerse.isPending"
      type="article, actions"
      max-height="180px"
      width="200px"
    )
</template>

<script setup lang="ts">
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import {
  type CommentaryType,
  knownCommentaryTypes,
  validCommentaryTypeShortNames,
} from "~/static/taxonomy/commentaryTypes";
import { injectRequired } from "~/injectRequired";
import { useCommentaryFiltersStore } from "~/stores/useCommentaryFiltersStore";
import { usePreferencesStore } from "~/stores/usePreferencesStore";
import { LoggerKey, ScriptureClientKey, SolrClientKey } from "~/injectionKeys";
import { getStringParam } from "~/routing/routes";
import { localesByCode } from "~/i18n/locales";
import { never } from "~/never";
import { useCommentaryQuery } from "~/composables/useCommentaryQuery";
import type { OuterCommentaryGroup } from "~/composables/processCommentarySearchResults/types";
import { logSsrErrorToSentry, type SerializableErrorInfo } from "~/errors/util";
import { getCountData } from "~/components/CommentaryType.util";
import TheCommentaryToolbar from "~/components/TheCommentaryToolbar.vue";

const i18n = useI18n();
const locale = i18n.locale;

definePageMeta({
  validate(route) {
    return validCommentaryTypeShortNames.includes(
      getStringParam(route, "type") || "",
    );
  },
  key: false, // This prevents recreating the `NuxtPage` component when we do `router.push`. We want to reuse the page.
  middleware: ["commentary"],
});

const scriptureClient = injectRequired(ScriptureClientKey);
const commentaryFiltersStore = useCommentaryFiltersStore();
const runtimeConfig = useRuntimeConfig();
const preferencesStore = usePreferencesStore();

const localeConfig = computed(
  () => localesByCode.get(i18n.locale.value) ?? never("Locale not found."),
);
const localTid = computed<string>(() => localeConfig.value.defaultTid);
const defaultCommentaryFilters: CommentaryFilters = {
  vids: runtimeConfig.public.commentary.defaultVids,
  detailLevelWeight: runtimeConfig.public.commentary.defaultDetailLevelWeight,
  commentaryTypeShortName: runtimeConfig.public.commentary.defaultType,
};

const vidRange = computed(() =>
  commentaryFiltersStore.getVidRange(defaultCommentaryFilters.vids),
);

const commentaryType = computed(() =>
  commentaryFiltersStore.getCommentaryType(
    defaultCommentaryFilters.commentaryTypeShortName,
  ),
);

const commentaryTypeShortName = computed<string>({
  get: () =>
    commentaryFiltersStore.commentaryTypeShortName ??
    defaultCommentaryFilters.commentaryTypeShortName,
  set(value) {
    commentaryFiltersStore.commentaryTypeShortName = value;
  },
});

const commentaryLanguages = computed<string[]>({
  get: () => preferencesStore.acceptedLanguages,
  set(value) {
    preferencesStore.acceptedLanguages = value;
  },
});

/**
 * A human-readable version of the VID range.
 */
const humanReadableRange = useComputedResultAsync(() =>
  vidRange.value.renderReference(scriptureClient, localTid.value, false, false),
);

/**
 * Info about the first verse in the VID range.
 */
const firstVerse = useComputedResultAsync(() =>
  vidRange.value
    .changeTranslation(localTid.value)
    .getFirstVerse(scriptureClient),
);

/**
 * The document title.
 */
const title = computed<string>(() => {
  if (humanReadableRange.value && commentaryType.value) {
    return `${humanReadableRange.value} ${commentaryType.value.name[locale.value]}`;
  }

  if (humanReadableRange.value) {
    return i18n.t("Commentary.comments", { count: humanReadableRange.value });
  }

  return commentaryType.value.name[locale.value] ?? "";
});

useHead({ title });

const logger = injectRequired(LoggerKey);
const solrClient = injectRequired(SolrClientKey);
const commentaryQuery = useCommentaryQuery(
  solrClient,
  (e, s) => logSsrErrorToSentry(logger, e, s),
  computed(() => commentaryFiltersStore.getFilters(defaultCommentaryFilters)),
  computed(() => preferencesStore.getLanguages(unref(i18n.locale))),
);

const commentaryQueryError = computed<Error | SerializableErrorInfo | null>(
  () =>
    commentaryQuery.error.value ?? commentaryQuery.data.value?.error ?? null,
);

const outerGroups = computed<OuterCommentaryGroup[]>(() => {
  return commentaryQuery.data.value?.value?.outerGroups ?? [];
});

const commentaryTypeCounts = computed<Map<string, Map<string, number>>>(
  () => commentaryQuery.data.value?.value?.commentaryTypeCounts ?? new Map(),
);

const detailLevelCounts = computed<Map<string, Map<number, number>>>(
  () => commentaryQuery.data.value?.value?.detailLevelCounts ?? new Map(),
);

const languageCounts = computed<Map<string, number>>(
  () => commentaryQuery.data.value?.value?.languageCounts ?? new Map(),
);

const otherLanguages = computed<string[]>(() =>
  preferencesStore.getOtherLanguages(unref(i18n.locale)),
);

const countData = computed(() => {
  return knownCommentaryTypes.reduce((acc: any, type: CommentaryType) => {
    const shortName = type.shortName;
    acc[shortName] = getCountData({
      shortName,
      locale: locale.value,
      counts: commentaryTypeCounts.value,
      otherLanguages: unref(otherLanguages),
    });
    return acc;
  }, {});
});

type NumResult = {
  totalCount: number;
  text: string;
};
const numResults = computed<NumResult>(() => {
  const { countTotal, countHtml } =
    countData.value[commentaryTypeShortName.value];

  return {
    totalCount: countTotal,
    text: countHtml,
  };
});

const otherCommentaryTypes = computed<CommentaryType[]>(() => {
  return knownCommentaryTypes
    .reduce((acc: Array<CommentaryType>, type: CommentaryType) => {
      const shortName = type.shortName;
      const count = countData.value?.[shortName].countTotal ?? 0;
      if (
        shortName !== commentaryFiltersStore.commentaryTypeShortName &&
        count > 0
      ) {
        acc.push(type);
      }
      return acc;
    }, [])
    .sort((typeA: CommentaryType, typeB: CommentaryType) => {
      return typeA.weight - typeB.weight;
    });
});

onServerPrefetch(async () => {
  // TODO:
  //await commentaryQuery.suspense();
});
</script>

<style lang="scss">
.commentary__chips {
  gap: 8px;
}
</style>
